/**
 * WEB-1123 — a toast's slot in the stack opens and closes in real layout.
 *
 * The stack used to animate with a FLIP (`layout` on each card under
 * `AnimatePresence mode="popLayout"`). A FLIP only moves pixels: when a toast
 * is sorted in above an open stack (a higher-priority arrival), or a queued
 * toast is promoted after a dismissal, every sibling jumps to its new slot in
 * layout and is then painted back at its OLD position and slid over 200ms —
 * while the entering card is already drawn in its final slot. For that window
 * the cards cover each other's text; once settled, the stack was always right.
 *
 * Each toast now sits in a slot whose height animates 0 ↔ auto. The column is
 * real layout on every frame, so siblings are pushed apart, never drawn over
 * each other, whichever edge the host anchors the region to.
 *
 * The host spaces the region's children with `gap`, which a zero-height slot
 * still receives. A closed slot therefore carries `margin-bottom: -gap` and
 * takes no room at all, so the column does not jump by a gap when a toast
 * opens or finishes closing. The gap is read from the host's
 * `.loci-toast-region` rule ({@link readStackGap}), never duplicated here.
 *
 * A closed or opening slot clips its card (`overflow: hidden`) so the card
 * cannot spill onto the next one; the shadow comes back once the slot is open.
 */
/** The region's row gap in px, or 0 when there is no region or no gap. */
export function readStackGap(region) {
    const view = region?.ownerDocument.defaultView;
    if (!region || !view)
        return 0;
    const gap = Number.parseFloat(view.getComputedStyle(region).rowGap);
    return Number.isFinite(gap) ? gap : 0;
}
/**
 * Slot variants. `closed` takes a gap reader as the slot's `custom` value.
 * framer-motion resolves it as each animation starts, so the slot always hands
 * back the host's current gap, and an exiting slot still holds the reader in
 * its own last props.
 *
 * `open` restates `overflow: 'hidden'` on purpose: framer-motion only applies
 * a `transitionEnd` value for a key the same target also names. Without it the
 * slot stays clipped at rest and cuts off the card's shadow.
 */
export const TOAST_SLOT_VARIANTS = {
    closed: (readGap) => ({ height: 0, marginBottom: -readGap(), overflow: 'hidden' }),
    open: {
        height: 'auto',
        marginBottom: 0,
        overflow: 'hidden',
        transitionEnd: { overflow: 'visible' },
    },
};
